import { STORAGE_KEYS } from "@/constants/storageKeys";
import { AppModule } from "@/enums/AppModule";
import { FORECAST_CONSTANTS } from "@/constants/forecastConstants";
import {
  IPracticeDetails,
  IUserPractice,
  IUserSite,
} from "../models/PracticeAssignment";
import { Site } from "../models/Site";
import { SpecialtyType } from "../models/SpecialtyType";
import { IRolesPermission, User, UserRole } from "../models/User";
import { apiClient, getCurrentAccessToken } from "./ApiClient";
import { getSessionStorageItem } from "@/utils/utils";

interface IUserPermissionSiteResponse {
  siteId: number;
  name: string;
}

interface IUserPermissionPracticeResponse {
  practiceId: number;
  displayName?: string;
  corpEntityId?: number;
  sites?: IUserPermissionSiteResponse[];
}

type ModulePracticeMap = Partial<Record<AppModule, IUserPractice[]>>;

const normalizeValue = (value: string): string => value.trim().toLowerCase();

const MODULE_SPECIALTY_KEY_MAP: Record<AppModule, Set<string>> = {
  [AppModule.DemandForecast]: new Set(
    (FORECAST_CONSTANTS.MODULE_SPECIALTY_KEYS.DEMAND_FORECAST || []).map(
      normalizeValue,
    ),
  ),
  [AppModule.AllocationCheckList]: new Set(
    (FORECAST_CONSTANTS.MODULE_SPECIALTY_KEYS.ALLOCATION_CHECK_LIST || []).map(
      normalizeValue,
    ),
  ),
  [AppModule.UsageForecast]: new Set(
    (FORECAST_CONSTANTS.MODULE_SPECIALTY_KEYS.USAGE_FORECAST || []).map(
      normalizeValue,
    ),
  ),
  [AppModule.RetinaForecast]: new Set(
    (FORECAST_CONSTANTS.MODULE_SPECIALTY_KEYS.RETINA_FORECAST || []).map(
      normalizeValue,
    ),
  ),
};

const getModulesForSpecialty = (specialtyKey: string): AppModule[] => {
  const normalizedKey = normalizeValue(specialtyKey);
  return (Object.values(AppModule) as AppModule[]).filter((module) =>
    MODULE_SPECIALTY_KEY_MAP[module]?.has(normalizedKey),
  );
};

const ensureModulePracticeList = (
  modulePracticeMap: ModulePracticeMap,
  module: AppModule,
): IUserPractice[] => {
  if (!modulePracticeMap[module]) {
    modulePracticeMap[module] = [];
  }
  return modulePracticeMap[module] as IUserPractice[];
};

const isModulePractice = (
  value: unknown,
): value is IUserPermissionPracticeResponse => {
  return (
    typeof value === "object" &&
    value !== null &&
    "practiceId" in value &&
    typeof (value as { practiceId: unknown }).practiceId === "number"
  );
};

const isModuleSite = (value: unknown): value is IUserPermissionSiteResponse => {
  return (
    typeof value === "object" &&
    value !== null &&
    "siteId" in value &&
    typeof (value as { siteId: unknown }).siteId === "number" &&
    "name" in value &&
    typeof (value as { name: unknown }).name === "string"
  );
};

type ModuleResponseMetadata = {
  id?: number;
  adoid?: string;
  corporateEntityId?: number;
  isActive?: boolean;
  contactEmail?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  usersRoles?: UserRole[];
  rolesPermissions?: IRolesPermission[];
};

const transformModuleBasedResponse = (
  payload: Record<string, unknown>,
  superUser?: boolean,
): User => {
  const modulePracticeMap: ModulePracticeMap = {};
  const practiceRegistry = new Map<number, IUserPractice>();

  Object.entries(payload).forEach(([specialtyKey, value]) => {
    if (!Array.isArray(value)) {
      return;
    }

    const modulesForSpecialty = getModulesForSpecialty(specialtyKey);
    if (modulesForSpecialty.length === 0) {
      return;
    }

    value.forEach((practiceValue) => {
      if (!isModulePractice(practiceValue)) {
        return;
      }

      const practiceName =
        typeof practiceValue.displayName === "string" &&
        practiceValue.displayName.trim().length > 0
          ? practiceValue.displayName.trim()
          : `Practice ${practiceValue.practiceId}`;

      const practiceSites: IUserSite[] = (practiceValue.sites ?? [])
        .filter(isModuleSite)
        .map((siteValue) => {
          const specialtyType = new SpecialtyType(0, specialtyKey, true);
          const siteModel = new Site(siteValue.siteId, false, specialtyType);
          return {
            siteId: siteValue.siteId,
            siteName: siteValue.name,
            displayName: siteValue.name,
            name: siteValue.name,
            isActive: true,
            practiceId: practiceValue.practiceId,
            practiceName,
            specialtyType,
            site: siteModel,
          };
        })
        .sort((a, b) => a.siteName.localeCompare(b.siteName));

      let practiceEntry = practiceRegistry.get(practiceValue.practiceId);
      if (!practiceEntry) {
        const practiceDetail: IPracticeDetails = {
          id: practiceValue.practiceId,
          name: practiceName,
          displayName: practiceValue.displayName ?? practiceName,
          practiceId: practiceValue.practiceId,
          accountNumber: "",
          isPracticeActive: true,
          corpEntityId:
            practiceValue.corpEntityId !== undefined
              ? practiceValue.corpEntityId
              : null,
          corpEntityAccountNumber: null,
        };

        practiceEntry = {
          practiceId: practiceValue.practiceId,
          practiceName,
          isActive: true,
          practiceDetail,
          practiceSites,
          specialtyKey,
          modules: [...modulesForSpecialty],
        };
        practiceRegistry.set(practiceValue.practiceId, practiceEntry);
      } else {
        const moduleSet = new Set(practiceEntry.modules ?? []);
        modulesForSpecialty.forEach((module) => moduleSet.add(module));
        practiceEntry.modules = Array.from(moduleSet);
        practiceEntry.specialtyKey = practiceEntry.specialtyKey ?? specialtyKey;

        if (practiceEntry.practiceDetail) {
          practiceEntry.practiceDetail.corpEntityId =
            practiceValue.corpEntityId ??
            practiceEntry.practiceDetail.corpEntityId ??
            null;
        }

        // Use a compound key (specialtyName|siteId) so the same siteId that
        // legitimately belongs to multiple specialties is preserved for each.
        const siteKey = (s: IUserSite) => `${s.specialtyType.name}|${s.siteId}`;
        const existingSitesMap = new Map(
          (practiceEntry.practiceSites ?? []).map((site) => [
            siteKey(site),
            site,
          ]),
        );
        practiceSites.forEach((site) => {
          const key = siteKey(site);
          if (!existingSitesMap.has(key)) {
            existingSitesMap.set(key, site);
          }
        });
        practiceEntry.practiceSites = Array.from(
          existingSitesMap.values(),
        ).sort((a, b) => a.siteName.localeCompare(b.siteName));
      }

      modulesForSpecialty.forEach((module) => {
        const list = ensureModulePracticeList(modulePracticeMap, module);
        if (
          !list.some(
            (practice) => practice.practiceId === practiceEntry!.practiceId,
          )
        ) {
          list.push(practiceEntry!);
        }
      });
    });
  });

  const userPractices = Array.from(practiceRegistry.values()).sort((a, b) =>
    a.practiceName.localeCompare(b.practiceName),
  );

  (Object.values(AppModule) as AppModule[]).forEach((module) => {
    const list = modulePracticeMap[module];
    if (list) {
      modulePracticeMap[module] = list.sort((a, b) =>
        a.practiceName.localeCompare(b.practiceName),
      );
    }
  });

  const { user: metadataCandidateRaw } = payload as {
    user?: ModuleResponseMetadata;
  };
  const metadataCandidate: ModuleResponseMetadata = metadataCandidateRaw ?? {};
  return new User(
    metadataCandidate?.id,
    metadataCandidate?.adoid,
    metadataCandidate?.corporateEntityId,
    metadataCandidate?.isActive ?? true,
    metadataCandidate?.contactEmail,
    metadataCandidate?.firstName,
    metadataCandidate?.lastName,
    metadataCandidate?.phoneNumber,
    undefined,
    undefined,
    undefined,
    undefined,
    userPractices,
    modulePracticeMap,
    superUser ?? false,
  );
};

const getUserAssignments = async () => {
  // Get the current access token
  const authToken = await getCurrentAccessToken();

  if (!authToken) {
    throw new Error("missing access token for authorization");
  }

  const customAuthToken = getSessionStorageItem(STORAGE_KEYS.UserContextToken);

  if (!customAuthToken) {
    throw new Error("missing custom authorization token for user permission");
  }

  const { data } = await apiClient.get("/auth/user_permission", {
    headers: {
      Authorization: `Bearer ${authToken}`,
      "custom-authorization": customAuthToken,
    },
  });
  const responseData = data?.data;
  const payload = responseData?.practice_assignments ?? responseData;
  const roleIds = (responseData?.roles ?? [])
    .map((role: { role_id: number }) => role.role_id)
    .join(", ");
  const superUser = roleIds.includes(FORECAST_CONSTANTS.SUPER_USER_ROLE_ID);

  if (typeof payload === "object" && payload !== null) {
    const recordPayload = payload as Record<string, unknown>;
    const hasArrayValues = Object.values(recordPayload).some((value) =>
      Array.isArray(value),
    );
    if (hasArrayValues) {
      return transformModuleBasedResponse(recordPayload, superUser);
    }
  }

  throw new Error("unsupported user permission response format");
};

const PermissionsService = {
  getUserAssignments,
};

export default PermissionsService;
